"use client";

import { Toaster as Sonner } from "sonner";

import { cn } from "../lib/utils";
import { useTheme } from "../providers/theme-provider";
import { buttonVariants } from "./button";
import { Icon } from "./icon";

type SonnerToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * API reference: https://sonner.emilkowal.ski/getting-started
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/sonner
 */
const SonnerToaster = ({ ...props }: SonnerToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      className="toaster group"
      icons={{
        success: <Icon icon="success" weight="fill" />,
        error: <Icon icon="error" weight="fill" />,
        warning: <Icon icon="warning" weight="fill" />,
        info: <Icon icon="info" weight="fill" />,
        loading: (
          <Icon className="animate-spin" icon="loading" weight="regular" />
        ),
      }}
      theme={theme as SonnerToasterProps["theme"]}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast:
            "group/toast shadow-lg p-4 text-sm flex gap-2 items-center rounded-lg border w-full [&:not([data-index='0'])>*]:data-[expanded=false]:opacity-0 [&>*]:transition-opacity",
          description: "text-muted-foreground dark:text-foreground",
          content: "mr-auto",

          actionButton: cn(
            buttonVariants({
              size: "xs",
            }),
          ),
          cancelButton: buttonVariants({
            variant: "transparent",
            size: "xs",
          }),
          closeButton:
            "!text-inherit !bg-inherit !border-inherit hover:brightness-95",

          default: "bg-background text-foreground border-border",
          success:
            "!text-ui-success bg-ui-success-background border-ui-success-border",
          error:
            "!text-ui-danger bg-ui-danger-background border-ui-danger-border",
          warning:
            "!text-ui-warning bg-ui-warning-background border-ui-warning-border",
          info: "!text-ui-info bg-ui-info-background border-ui-info-border",
        },
      }}
      {...props}
    />
  );
};

export { SonnerToaster, type SonnerToasterProps };
